import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import classNames from "classnames"
import webify from "../util/webify"
import SVG from "react-inlinesvg"
import debounce from "lodash/debounce"
import * as Money from "../util/money.js"

import cornerdownright from "../../../priv/static/svg/generic/corner-down-right.svg"
import filterSvg from "../../../priv/static/svg/generic/filter.svg"
import chevronLeft from "../../../priv/static/svg/generic/chevron-left.svg"
import { getFilteredData } from "../util/getFilteredData"
import { AdvancedFilterState, ColumnFilterState, setAdvancedFilter, toQuery } from "../models/FilterState"
import FilterTabs from "./FilterTabs"
import NumberFormat, { NumberFormatValues } from "react-number-format"
import Document from "../models/Document"
import ColumnFilters from "./ColumnFilters"
import { typeNoun } from "@models/List"
import capitalize from "lodash/capitalize"

interface AdvancedFiltersProps {
  showCustom: boolean
  showColumnFilters?: boolean
  initialFilters: AdvancedFilterState
  insuranceEnabled: boolean
  insuranceProductLabel: string
  requiredDocuments: any[]
  questionnaires: any[]
  addons: any[]
  packages: any[]
  packageTypes: any[]
  lists: any[]
}

const AdvancedFilters = (props: AdvancedFiltersProps) => {
  let [filters, setFilters] = useState({ ...props.initialFilters, advanced: true })
  let setFiltersDebounced = debounce(setFilters, 500)
  const requiredDocuments = props.requiredDocuments.map((requiredDoc) => new Document(requiredDoc))

  useEffect(() => {
    getFilteredData(toQuery(filters))
  })

  return (
    <div>
      <div className="filters-component">
        <FilterTabs showCustom={props.showCustom} />

        <div className="filter-column">
          <h3 className="mb-1">Registration Info</h3>
          <ul>
            <UnsignedDocumentFilter
              {...props}
              requiredDocuments={requiredDocuments}
              filters={filters}
              setFilters={setFilters}
            />
            <MissingAnswerFilter {...props} filters={filters} setFilters={setFilters} />
            <MissingInfoFilter {...props} filters={filters} setFilters={setFilters} />
          </ul>
        </div>

        <div className="filter-column">
          <h3 className="mb-1">Trip Details</h3>
          <ul>
            {props.addons.length > 0 && (
              <>
                <WithAddonFilter {...props} filters={filters} setFilters={setFilters} />
                <WithoutAddonFilter {...props} filters={filters} setFilters={setFilters} />
              </>
            )}
            {props.packages.length > 0 && <OnPackageFilter {...props} filters={filters} setFilters={setFilters} />}
            {props.packageTypes.length > 0 && <OnPackageTypeFilter {...props} filters={filters} setFilters={setFilters} />}
            {props.insuranceEnabled && <InsurancePlanFilter {...props} filters={filters} setFilters={setFilters} />}
            <AutopayEnabledFilter {...props} filters={filters} setFilters={setFilters} />
            <PaymentStatusFilter {...props} filters={filters} setFilters={setFilters} />
            <CancelledWithNonRefundablesFilter {...props} filters={filters} setFilters={setFilters} />
            <PaymentAmountFilters
              {...props}
              filters={filters}
              setFilters={setFilters}
              setFiltersDebounced={setFiltersDebounced}
            />
          </ul>
        </div>
        <div className="filter-column">
          <h3 className="mb-1">Passenger Groups</h3>
          <ul>
            <AgeFilters {...props} filters={filters} setFilters={setFilters} setFiltersDebounced={setFiltersDebounced} />
            {props.lists.map((list) => (
              <ListSectionFilter {...props} filters={filters} setFilters={setFilters} list={list} />
            ))}
            <TripLeaderFilter {...props} filters={filters} setFilters={setFilters} />
            <CompedStatusFilter {...props} filters={filters} setFilters={setFilters} />
          </ul>
        </div>
      </div>
      {props.showColumnFilters && (
        <div className="mt-1">
          <ColumnFilters questionnaires={props.questionnaires} filters={filters} setFilters={setFilters} />
        </div>
      )}
    </div>
  )
}

interface FilterProps extends AdvancedFiltersProps {
  filters: AdvancedFilterState
  setFilters: Dispatch<SetStateAction<AdvancedFilterState>>
  setFiltersDebounced?: Dispatch<SetStateAction<AdvancedFilterState>>
}

function UnsignedDocumentFilter({ filters, setFilters, requiredDocuments }: FilterProps) {
  return (
    <Toggle
      open={filters.unsignedDocument !== undefined}
      data-role="unsigned-document-toggle"
      setFilters={setFilters}
      filters={filters}
      filter={{ unsignedDocument: "any" }}
      name="Missing Signatures">
      <Filter
        key="any"
        active={filters.unsignedDocument === "any"}
        data-role="any-unsigned-document"
        setFilters={setFilters}
        filters={filters}
        filter={{ unsignedDocument: "any" }}
        name="Any Document"></Filter>
      {requiredDocuments.map((doc) => (
        <Filter
          key={doc.id}
          data-role={`unsigned-document-${doc.id}`}
          active={filters.unsignedDocument === doc.id}
          setFilters={setFilters}
          filters={filters}
          filter={{ unsignedDocument: doc.id }}
          name={doc.name}></Filter>
      ))}
    </Toggle>
  )
}

function MissingAnswerFilter({ filters, setFilters, questionnaires }: FilterProps) {
  return (
    <>
      {questionnaires.map((questionnaire) => (
        <Toggle
          open={filters.questionnaireId == questionnaire.id}
          data-role="missing-answer-toggle"
          setFilters={setFilters}
          filter={{ questionnaireId: questionnaire.id, missingAnswer: "any" }}
          filters={filters}
          name={`Unanswered: ${questionnaire.name}`}>
          <Filter
            key="any"
            active={filters.missingAnswer === "any"}
            data-role="any-missing-answer"
            setFilters={setFilters}
            filter={{ questionnaireId: questionnaire.id, missingAnswer: "any" }}
            filters={filters}
            name="Any Question"></Filter>
          {questionnaire.questions.map((question) => (
            <Filter
              key={question.id}
              active={filters.missingAnswer === question.id}
              data-role={`missing-answer-${question.id}`}
              setFilters={setFilters}
              filter={{ questionnaireId: questionnaire.id, missingAnswer: question.id }}
              filters={filters}
              name={question.label}></Filter>
          ))}
        </Toggle>
      ))}
    </>
  )
}

function MissingInfoFilter({ filters, setFilters }: FilterProps) {
  return (
    <Toggle
      open={filters.missingInfo !== undefined}
      data-role="missing-info-toggle"
      setFilters={setFilters}
      filter={{ missingInfo: "any" }}
      filters={filters}
      name="Missing Information">
      <Filter
        key="any"
        active={filters.missingInfo === "any"}
        data-role="missing-any-info"
        setFilters={setFilters}
        filters={filters}
        filter={{ missingInfo: "any" }}
        name="Missing Information (excluding billing info)"></Filter>
      <Filter
        key="billing"
        active={filters.missingInfo === "missing_billing_info"}
        data-role="missing-billing-info"
        setFilters={setFilters}
        filters={filters}
        filter={{ missingInfo: "missing_billing_info" }}
        name="Missing Billing Information"></Filter>
    </Toggle>
  )
}

function WithAddonFilter({ filters, setFilters, addons }: FilterProps) {
  return (
    <Toggle
      open={filters.withAddon !== undefined}
      data-role="with-addon-toggle"
      setFilters={setFilters}
      filter={{ withAddon: "any" }}
      filters={filters}
      name="With Add-on">
      <Filter
        key="any"
        active={filters.withAddon === "any"}
        data-role="with-addon-any"
        setFilters={setFilters}
        filter={{ withAddon: "any" }}
        filters={filters}
        name="Any Add-ons"></Filter>
      {addons.map((addon) => (
        <Filter
          key={addon.id}
          active={filters.withAddon === addon.id}
          data-role={`with-addon-${addon.id}`}
          setFilters={setFilters}
          filter={{ withAddon: addon.id }}
          filters={filters}
          name={addon.name}></Filter>
      ))}
    </Toggle>
  )
}

function WithoutAddonFilter({ filters, setFilters, addons }: FilterProps) {
  return (
    <Toggle
      open={filters.withoutAddon !== undefined}
      data-role="without-addon-toggle"
      setFilters={setFilters}
      filter={{ withoutAddon: addons[0].id }}
      filters={filters}
      name="Without Add-on">
      {addons.map((addon) => (
        <Filter
          key={addon.id}
          active={filters.withoutAddon === addon.id}
          data-role={`without-addon-${addon.id}`}
          setFilters={setFilters}
          filter={{ withoutAddon: addon.id }}
          filters={filters}
          name={addon.name}></Filter>
      ))}
    </Toggle>
  )
}

function OnPackageFilter({ filters, setFilters, packages }: FilterProps) {
  return (
    <Toggle
      open={filters.onPackage !== undefined}
      data-role="on-package-toggle"
      setFilters={setFilters}
      filter={{ onPackage: packages[0].id }}
      filters={filters}
      name="Package Selection">
      {packages.map((pkg) => (
        <Filter
          key={pkg.id}
          active={filters.onPackage === pkg.id}
          data-role={`on-package-${pkg.id}`}
          setFilters={setFilters}
          filter={{ onPackage: pkg.id }}
          filters={filters}
          name={pkg.name}></Filter>
      ))}
      <Filter
        key={"no-package"}
        active={filters.onPackage === null}
        data-role={`on-package-no-package`}
        setFilters={setFilters}
        filter={{ onPackage: null }}
        filters={filters}
        name="No Package"></Filter>
    </Toggle>
  )
}

function OnPackageTypeFilter({ filters, setFilters, packageTypes }: FilterProps) {
  return (
    <Toggle
      open={filters.onPackageType !== undefined}
      data-role="on-package-type-toggle"
      setFilters={setFilters}
      filter={{ onPackageType: packageTypes[0].id }}
      filters={filters}
      name="Package Type">
      {packageTypes.map((pkgType) => (
        <Filter
          key={pkgType.id}
          active={filters.onPackageType === pkgType.id}
          data-role={`on-package-type-${pkgType.id}`}
          setFilters={setFilters}
          filter={{ onPackageType: pkgType.id }}
          filters={filters}
          name={pkgType.name}></Filter>
      ))}
      <Filter
        key={"no-package-type"}
        active={filters.onPackageType === null}
        data-role={`on-package-type-no-package-type`}
        setFilters={setFilters}
        filter={{ onPackageType: null }}
        filters={filters}
        name="No Package Type"></Filter>
    </Toggle>
  )
}

function InsurancePlanFilter({ filters, setFilters, insuranceProductLabel }: FilterProps) {
  return (
    <Toggle
      open={filters.insurancePlan !== undefined}
      data-role="insurance-plan-toggle"
      setFilters={setFilters}
      filter={{ insurancePlan: null }}
      filters={filters}
      name="Trip Protection">
      <Filter
        key="none"
        active={filters.insurancePlan === null}
        data-role="no-insurance-plan"
        setFilters={setFilters}
        filter={{ insurancePlan: null }}
        filters={filters}
        name="No Selection Made"></Filter>

      <Filter
        key="standard"
        active={filters.insurancePlan === "standard"}
        data-role="standard-insurance-plan"
        setFilters={setFilters}
        filter={{ insurancePlan: "standard" }}
        filters={filters}
        name={`${insuranceProductLabel} Standard`}></Filter>
      <Filter
        key="enhanced"
        active={filters.insurancePlan === "enhanced"}
        data-role="enhanced-insurance-plan"
        setFilters={setFilters}
        filter={{ insurancePlan: "enhanced" }}
        filters={filters}
        name={`${insuranceProductLabel} Enhanced`}></Filter>
      <Filter
        key="none"
        active={filters.insurancePlan === "any"}
        data-role="any-insurance-plan"
        setFilters={setFilters}
        filter={{ insurancePlan: "any" }}
        filters={filters}
        name={`Any ${insuranceProductLabel} Plan`}></Filter>
      <Filter
        key="declined"
        active={filters.insurancePlan === "declined"}
        data-role="declined-insurance"
        setFilters={setFilters}
        filter={{ insurancePlan: "declined" }}
        filters={filters}
        name="Declined"></Filter>
    </Toggle>
  )
}

function AutopayEnabledFilter({ filters, setFilters }: FilterProps) {
  return (
    <Toggle
      open={filters.autopayEnabled !== undefined}
      data-role="autopay-enabled-toggle"
      setFilters={setFilters}
      filter={{ autopayEnabled: null }}
      filters={filters}
      name="AutoPay Status">
      <Filter
        key="none"
        active={filters.autopayEnabled === null}
        data-role="no-autopay-selection"
        setFilters={setFilters}
        filter={{ autopayEnabled: null }}
        filters={filters}
        name="No Selection Made"></Filter>
      <Filter
        key="none"
        active={filters.autopayEnabled === true}
        data-role="autopay-enabled"
        setFilters={setFilters}
        filter={{ autopayEnabled: true }}
        filters={filters}
        name="Enabled"></Filter>
      <Filter
        key="none"
        active={filters.autopayEnabled === false}
        data-role="autopay-disabled"
        setFilters={setFilters}
        filter={{ autopayEnabled: false }}
        filters={filters}
        name="Disabled"></Filter>
    </Toggle>
  )
}

function PaymentStatusFilter({ filters, setFilters }: FilterProps) {
  return (
    <Toggle
      open={filters.paymentStatus !== undefined}
      data-role="payment-status-toggle"
      setFilters={setFilters}
      filter={{ paymentStatus: "partially_paid" }}
      filters={filters}
      name="Payment Status">
      <Filter
        key="none"
        active={filters.paymentStatus === "partially_paid"}
        data-role="payment-status-partially-paid"
        setFilters={setFilters}
        filter={{ paymentStatus: "partially_paid" }}
        filters={filters}
        name="Partially Paid"></Filter>
      <Filter
        key="none"
        active={filters.paymentStatus === "fully_paid"}
        data-role="payment-status-fully-paid"
        setFilters={setFilters}
        filter={{ paymentStatus: "fully_paid" }}
        filters={filters}
        name="Fully Paid"></Filter>
      <Filter
        key="none"
        active={filters.paymentStatus === "overpaid"}
        data-role="payment-status-overpaid"
        setFilters={setFilters}
        filter={{ paymentStatus: "overpaid" }}
        filters={filters}
        name="Refund Due"></Filter>
      <Filter
        key="none"
        active={filters.paymentStatus === "cancelled_with_overages"}
        data-role="cancelled-with-overages"
        setFilters={setFilters}
        filter={{ paymentStatus: "cancelled_with_overages" }}
        filters={filters}
        name="Cancelled With Overages"></Filter>
    </Toggle>
  )
}

function CancelledWithNonRefundablesFilter({ filters, setFilters, packages }: FilterProps) {
  return (
    <Toggle
      open={filters.cancelledWithNonRefundables !== undefined}
      data-role="cancelled-with-non-refundables-toggle"
      setFilters={setFilters}
      filter={{ cancelledWithNonRefundables: packages[0].id }}
      filters={filters}
      name="Cancelled with Non-Refundables">
      {packages.map((pkg) => (
        <Filter
          key={pkg.id}
          active={filters.cancelledWithNonRefundables === pkg.id}
          data-role={`cancelled-with-non-refundables-on-package-${pkg.id}`}
          setFilters={setFilters}
          filter={{ cancelledWithNonRefundables: pkg.id }}
          filters={filters}
          name={pkg.name}></Filter>
      ))}
      <Filter
        key={"none"}
        active={filters.cancelledWithNonRefundables === "none"}
        data-role={`cancelled-with-non-refundables-on-package-none`}
        setFilters={setFilters}
        filter={{ cancelledWithNonRefundables: "none" }}
        filters={filters}
        name={"No Package"}></Filter>
    </Toggle>
  )
}

function paymentAmountValue(filters: AdvancedFilterState): number {
  if (filters.paidAtLeast !== undefined) return filters.paidAtLeast
  if (filters.paidLessThan !== undefined) return filters.paidLessThan
  return 100_00
}

function PaymentAmountFilters({ filters, setFilters, setFiltersDebounced }: FilterProps) {
  let [paymentAmount, setPaymentAmount] = useState(paymentAmountValue(filters))

  return (
    <Toggle
      open={filters.paidAtLeast !== undefined || filters.paidLessThan !== undefined}
      data-role="payment-amount-toggle"
      setFilters={setFilters}
      filter={{ paidLessThan: 100_00 }}
      filters={filters}
      name="Amount Paid">
      <>
        <ul>
          <li className="flex mt-2">
            <select
              name="amount-paid-filter"
              id="amount-paid-filter"
              value={filters.paidLessThan !== undefined ? "paidLessThan" : "paidAtLeast"}
              data-role="payment-amount-filter"
              onChange={(e) => {
                if (e.target.value === "paidAtLeast")
                  setFilters(setAdvancedFilter(filters, { paidAtLeast: paymentAmountValue(filters) }))
                if (e.target.value === "paidLessThan")
                  setFilters(setAdvancedFilter(filters, { paidLessThan: paymentAmountValue(filters) }))
              }}>
              <option value="paidLessThan" data-role="paid-less-than">
                Less than
              </option>
              <option value="paidAtLeast" data-role="paid-at-least">
                At Least
              </option>
            </select>
            <NumberFormat
              thousandSeparator={true}
              prefix={"$"}
              allowNegative={false}
              value={Money.formatCentsAsUsd(paymentAmount)}
              onValueChange={(value: NumberFormatValues) => {
                let newValue = Money.parseDollarsToCents(value.formattedValue) || 0
                setPaymentAmount(newValue)
                filters.paidLessThan !== undefined
                  ? setFiltersDebounced(setAdvancedFilter(filters, { paidLessThan: newValue }))
                  : setFiltersDebounced(setAdvancedFilter(filters, { paidAtLeast: newValue }))
              }}
              data-role="payment-amount-input"
              className="text-right"
            />
          </li>
        </ul>
      </>
    </Toggle>
  )
}

function filterAge(filters: AdvancedFilterState): number {
  if (filters.ageAtLeast !== undefined) return filters.ageAtLeast
  if (filters.ageLessThan !== undefined) return filters.ageLessThan
  return 18
}

function AgeFilters({ filters, setFilters, setFiltersDebounced }: FilterProps) {
  let [age, setAge] = useState(filterAge(filters).toString())

  return (
    <Toggle
      open={filters.ageAtLeast !== undefined || filters.ageLessThan !== undefined}
      data-role="age-toggle"
      setFilters={setFilters}
      filter={{ ageAtLeast: 18 }}
      filters={filters}
      name="Filter by Age">
      <>
        <ul>
          <li className="flex mt-2">
            <select
              name="age-filter"
              id="age-filter"
              value={filters.ageLessThan !== undefined ? "ageLessThan" : "ageAtLeast"}
              data-role="age-filter"
              onChange={(e) => {
                if (e.target.value === "ageAtLeast")
                  setFilters(setAdvancedFilter(filters, { ageAtLeast: filterAge(filters) }))
                if (e.target.value === "ageLessThan")
                  setFilters(setAdvancedFilter(filters, { ageLessThan: filterAge(filters) }))
              }}>
              <option value="ageAtLeast">At Least</option>
              <option value="ageLessThan">Less Than</option>
            </select>
            <input
              type="text"
              value={age}
              data-role="age-input"
              onChange={(e) => {
                setAge(e.target.value)
                let newAge = parseInt(e.target.value)

                if (!Number.isNaN(newAge)) {
                  filters.ageLessThan !== undefined
                    ? setFiltersDebounced(setAdvancedFilter(filters, { advanced: true, ageLessThan: newAge }))
                    : setFiltersDebounced(setAdvancedFilter(filters, { advanced: true, ageAtLeast: newAge }))
                }
              }}></input>
          </li>
        </ul>
        <small>Based on passenger age when the trip starts.</small>
      </>
    </Toggle>
  )
}

function TripLeaderFilter({ filters, setFilters }: FilterProps) {
  return (
    <Filter
      key="none"
      active={filters.tripLeader === true}
      data-role="trip-leaders"
      setFilters={setFilters}
      filters={filters}
      filter={{ tripLeader: true }}
      name="Trip Leaders Only"
      hideArrow={true}></Filter>
  )
}

function CompedStatusFilter({ filters, setFilters }: FilterProps) {
  return (
    <Toggle
      open={filters.compedStatus !== undefined}
      data-role="comped-status-toggle"
      setFilters={setFilters}
      filter={{ compedStatus: "all" }}
      filters={filters}
      name="Comped">
      <Filter
        key="none"
        active={filters.compedStatus === "all"}
        data-role="comped-status-all"
        setFilters={setFilters}
        filter={{ compedStatus: "all" }}
        filters={filters}
        name="All"></Filter>
      <Filter
        key="none"
        active={filters.compedStatus === "current"}
        data-role="comped-status-current"
        setFilters={setFilters}
        filter={{ compedStatus: "current" }}
        filters={filters}
        name="Current"></Filter>
      <Filter
        key="none"
        active={filters.compedStatus === "behind"}
        data-role="comped-status-behind"
        setFilters={setFilters}
        filter={{ compedStatus: "behind" }}
        filters={filters}
        name="Behind"></Filter>
      <Filter
        key="none"
        active={filters.compedStatus === "pending"}
        data-role="comped-status-pending"
        setFilters={setFilters}
        filter={{ compedStatus: "pending" }}
        filters={filters}
        name="Pending"></Filter>
    </Toggle>
  )
}

interface ListSectionFilterProps extends FilterProps {
  list: any
}

function ListSectionFilter({ filters, setFilters, list }: ListSectionFilterProps) {
  let sortedSections = list.sections.sort((section1, section2) => section1.number - section2.number)
  let sectionIds = sortedSections.map((section) => section.id)
  if (sectionIds.length == 0) return null

  return (
    <Toggle
      open={sectionIds.includes(filters.onListSection)}
      data-role={`list-toggle-${list.id}`}
      setFilters={setFilters}
      filter={{ onListSection: sectionIds[0] }}
      filters={filters}
      name={list.name}>
      <select
        value={filters.onListSection}
        onChange={(e) => setFilters(setAdvancedFilter(filters, { onListSection: parseInt(e.target.value) }))}>
        {sortedSections.map((section) => (
          <option value={section.id} data-role={`on-list-section-${section.id}`}>
            {capitalize(typeNoun(list))} {section.number} {section.name && `(${section.name})`}
          </option>
        ))}
      </select>
    </Toggle>
  )
}

interface ToggleProps {
  open: boolean
  setFilters: Dispatch<SetStateAction<AdvancedFilterState>>
  filters: AdvancedFilterState
  filter: AdvancedFilterState
  name: string
  children?: any
  "data-role"?: string
}

function Toggle(props: ToggleProps) {
  let { open, setFilters, filters, filter, name } = props
  return (
    <li>
      <div
        className={classNames("toggle", { open: open })}
        data-role={props["data-role"]}
        onClick={() => setFilters(setAdvancedFilter(filters, filter))}>
        <span className="toggle-name">{name}</span>
        <SVG src={chevronLeft} className="chevron-svg" />
      </div>
      {open && <ul className="toggle-body">{props.children}</ul>}
    </li>
  )
}

interface FilterElementProps {
  key: number | string
  active: boolean
  setFilters: Dispatch<SetStateAction<AdvancedFilterState>>
  filters: AdvancedFilterState
  filter: AdvancedFilterState
  name: string
  hideArrow?: boolean
  "data-role"?: string
}

function Filter(props: FilterElementProps) {
  let { key, active, setFilters, filters, filter, name, hideArrow } = props
  return (
    <li
      key={key}
      className={classNames("filter", { active: active })}
      data-role={props["data-role"]}
      onClick={() => setFilters(setAdvancedFilter(filters, filter))}>
      {!hideArrow && <SVG src={cornerdownright} className="corner-down-right" />}
      <span className="filter-name">{name}</span>
      <SVG src={filterSvg} className="filter-svg" />
    </li>
  )
}

export default AdvancedFilters
webify("advanced-filters", AdvancedFilters)
